import { useEffect, useMemo, useRef } from 'react'
import { useLocation, useParams } from 'react-router'
import { ChatPanel } from 'tailwind-v4/blocks/agent-chat'
import { ToolRendererProvider } from 'tailwind-v4/blocks/chat'
import { useDurableChatThreads } from '@/components/durable-chat/use-durable-chat-threads'
import { composeAppContext } from '@/components/agent-chat-dock/compose-app-context'
import { useKnowledgeBaseTransport } from '../chat-3/use-knowledge-base-transport'
import { initKnowledgeBaseToolRegistrations } from './tool-cards/tool-renderers'
import { KNOWLEDGE_BASE_AGENT } from './agent-descriptor'
import { useKnowledgeBasePanel } from '@/providers/knowledge-base-panel.context'

const AGENTS = [KNOWLEDGE_BASE_AGENT]

/** Deliberately flush against the viewport edge (no floating-surface
 * inset) — exported so `layout.tsx` can reserve the same amount of
 * space via `padding-right` without duplicating this panel's own
 * constants. */
export const KNOWLEDGE_BASE_PANEL_WIDTH = 420
const KNOWLEDGE_BASE_PANEL_INSET = 0
export const KNOWLEDGE_BASE_PANEL_RESERVED_WIDTH =
  KNOWLEDGE_BASE_PANEL_WIDTH + KNOWLEDGE_BASE_PANEL_INSET * 2

/**
 * Docked, dismissible Knowledge Base chat — mounted once at the org layout
 * level (`layout.tsx`) so it can float above ANY page, not just the
 * dedicated `/knowledge-base` destination. Open/closed and which thread
 * come from `useKnowledgeBasePanel()` (panel-context.tsx), NOT the URL —
 * that Provider is what actually survives in-app navigation; the URL is
 * only its one-time seed for a deep link or reload. "Go to page →" links
 * inside a Knowledge Base answer (search-knowledge-card.tsx) call
 * `openPanel(threadId)` directly rather than encoding it in the href.
 *
 * `KnowledgeBaseSidePanelInner` (and its thread-list fetch) only mounts
 * once the panel is actually open, so pages that never open it never pay
 * for it.
 */
export function KnowledgeBaseSidePanel() {
  const { isOpen } = useKnowledgeBasePanel()
  if (!isOpen) return null
  return <KnowledgeBaseSidePanelInner />
}

function KnowledgeBaseSidePanelInner() {
  useMemo(() => initKnowledgeBaseToolRegistrations(), [])

  const { org_id: orgId = '' } = useParams<{ org_id: string }>()
  const {
    threadId,
    setThreadId,
    closePanel,
    pendingFollowUp,
    clearPendingFollowUp,
  } = useKnowledgeBasePanel()
  const transport = useKnowledgeBaseTransport(orgId)
  // The panel is mounted once at the layout level and stays mounted across
  // navigation — `pathname` here is whatever HOST page the panel is
  // currently floating over, changing as the user moves around the app.
  // This is exactly the "where is the user right now" signal
  // knowledge-base.ts's NAVIGATION AWARENESS block needs.
  const { pathname } = useLocation()

  const state = useDurableChatThreads({
    transport,
    basePath: '/agents/knowledge-base',
    agent: KNOWLEDGE_BASE_AGENT,
    activeThreadId: threadId,
    // Fires only when the panel was opened with no thread yet and the
    // first message just created one.
    onThreadChange: setThreadId,
    appContext: composeAppContext({ pathname }),
  })

  // Auto-sends a queued follow-up (from a "Go to page →" link) once the
  // panel has actually landed here — waits for the thread's own history
  // to settle first (avoids racing `loadThread()`'s overwrite of
  // `r.messages`) and for any in-flight send to finish. The ref stops a
  // re-render from re-sending the SAME queued prompt before the context
  // clears it.
  const sentFollowUpRef = useRef<string | null>(null)
  useEffect(() => {
    if (!pendingFollowUp) return
    if (sentFollowUpRef.current === pendingFollowUp) return
    if (state.messagesLoading) return
    if (state.status === 'streaming' || state.status === 'submitting') return
    sentFollowUpRef.current = pendingFollowUp
    void state.send(pendingFollowUp)
    clearPendingFollowUp()
  }, [
    pendingFollowUp,
    state.messagesLoading,
    state.status,
    state.send,
    clearPendingFollowUp,
  ])

  return (
    <ToolRendererProvider
      options={{ knowledgeBaseThreadId: state.activeThread?.id }}
    >
      <ChatPanel
        open
        onOpenChange={(next: boolean) => {
          if (!next) closePanel()
        }}
        inset={KNOWLEDGE_BASE_PANEL_INSET}
        showAura={false}
        frosted={false}
        // Flat dock, not a floating AI surface: no corner radius, no ring,
        // no drop shadow — just a single hairline on the left, the same
        // `--border` token the sidebar's right edge (border-sidebar-border)
        // and the top nav's bottom edge (border-border) both already use.
        // Solid `bg-background` (via `frosted={false}`) matches the rest of
        // the app's chrome instead of a translucent glass panel.
        className="rounded-none ring-0 shadow-none border-l border-border bg-background"
        // The composer hardcodes its own `bg-background` box by default —
        // matches the aside above so there's no visible seam around it.
        composerClassName="bg-background"
        agent={KNOWLEDGE_BASE_AGENT}
        agents={AGENTS}
        onSelectAgent={() => {}}
        messages={state.messages}
        status={state.status}
        progress={state.progress}
        attachments={state.attachments}
        onSendMessage={async (text: string) => {
          await state.send(text)
        }}
        onStop={state.stop}
        onAttachFiles={async () => {
          // Attachments aren't part of the Knowledge Base agent's scope
          // (knowledge-base-agent-prd.md never calls for them) — no-op.
        }}
        onRemoveAttachment={state.removeAttachment}
      />
    </ToolRendererProvider>
  )
}
